import React from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import './DatePickerStyles.css'
import { Flex, Input, Text, Tooltip, Box } from '@chakra-ui/react'
import { Field } from 'react-final-form'
import { InfoIcon } from '@chakra-ui/icons'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
dayjs.extend(customParseFormat)

interface Props {
  name: string
  label: string
  validate?: any
  description?: string
  placeholder?: string
  outputFormat?: 'timestamp' | 'DD.MM.YYYY'
}

const DateField: React.FC<Props> = ({
  name,
  label,
  validate,
  description,
  placeholder = 'DD.MM.YYYY',
  outputFormat = 'timestamp'
}) => {
  return (
    <Field
      name={name}
      validate={validate}
      render={({ input, meta }) => {
        const value = input.value
        // Parse the form value into a Date object for the picker.
        // Values may be DD.MM.YYYY strings (normal input) or Unix timestamps
        // (legacy data stored via Date.now() default in VisitServiceImpl).
        const selectedDate = (() => {
          if (!value) return null
          if (
            typeof value === 'number' ||
            (typeof value === 'string' && /^\d+$/.test(value))
          ) {
            const d = dayjs(Number(value))
            return d.isValid() ? d.toDate() : null
          }
          let d = dayjs(value, 'DD.MM.YYYY')
          if (!d.isValid()) d = dayjs(value, 'YYYY-MM-DD')
          return d.isValid() ? d.toDate() : null
        })()

        const handleDateChange = (date: Date | null) => {
          if (!date) { input.onChange(null); return; }
          input.onChange(
            outputFormat === 'DD.MM.YYYY'
              ? dayjs(date).format('DD.MM.YYYY')
              : date.getTime()
          )
        }

        return (
          <Flex flexDir="column" gap={1}>
            <Flex alignItems="center" gap={1}>
              <Text fontSize="sm" fontWeight="medium">
                {label} {validate && '*'}
              </Text>
              {description && (
                <Tooltip label={description}>
                  <InfoIcon boxSize={3} color="gray.500" />
                </Tooltip>
              )}
            </Flex>
            <Box width="100%">
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                dateFormat="dd.MM.yyyy"
                placeholderText={placeholder}
                autoComplete="off"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                maxDate={new Date()}
                customInput={
                  <Input
                    size="md"
                    variant="outline"
                    isInvalid={meta.touched && !!meta.error}
                    _focus={{
                      borderColor: 'blue.500',
                      boxShadow: '0 0 0 1px blue.500'
                    }}
                  />
                }
                // Ensure the popper is contained within the portal if needed, but removing for now as root-portal doesn't exist
              />
            </Box>
            {meta.touched && meta.error && (
              <Text color="red.500" fontSize="xs">
                {meta.error}
              </Text>
            )}
          </Flex>
        )
      }}
    />
  )
}

export default DateField
